'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  InlineDialog,
  InlineDialogHeader,
  InlineDialogFooter,
  InlineDialogTitle,
  InlineDialogDescription,
} from './inline-dialog'
import { X, Copy, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { m, AnimatePresence } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { useDemo } from '../demo-context'
import { useDemoActions } from '../use-demo-actions'
import { useDemoSession } from '../use-demo-session-tracking'
import type { DemoSong, DemoQueueRequest } from '../demo-types'
import { ReactionBubbles, ReactionBubblesStyles } from './reaction-bubbles'
import { StatsHeader } from './stats-header'
import { NowPlayingControls } from './now-playing-controls'
import { QueueManagement } from './queue-management'
import { RepertoireSearch } from './repertoire-search'
import { SetlistPanel } from './setlist-panel'
import { BreakControls } from './break-controls'
import { TimersPanel } from './timers-panel'
import { NotesPanel } from './notes-panel'
import { SettingsPanel } from './settings-panel'
import { GoLiveView } from './go-live-view'

export function DemoArtistConsole() {
  const { state, dispatch, sortedQueue, activeTimers } = useDemo()
  const actions = useDemoActions(dispatch)
  const demoSession = useDemoSession()
  const t = useTranslations('demo')

  // Dialog state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isBreakDialogOpen, setIsBreakDialogOpen] = useState(false)
  const [isTimerDialogOpen, setIsTimerDialogOpen] = useState(false)
  const [isNotesOpen, setIsNotesOpen] = useState(false)
  const [isSongHistoryOpen, setIsSongHistoryOpen] = useState(false)
  const [isQRDialogOpen, setIsQRDialogOpen] = useState(false)
  const [isEndEventDialogOpen, setIsEndEventDialogOpen] = useState(false)
  const [isAllRequestsOpen, setIsAllRequestsOpen] = useState(false)
  const [confirmSkipRequest, setConfirmSkipRequest] = useState<DemoQueueRequest | null>(null)
  const [selectedSetlistId, setSelectedSetlistId] = useState<string | null>(null)

  // QR code
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  useEffect(() => {
    const url = typeof window !== 'undefined' ? `${window.location.origin}/demo` : 'https://iperform.live/demo'
    import('qrcode').then(QRCode => QRCode.default.toDataURL(url, {
      width: 256,
      margin: 2,
      color: { dark: '#18181b', light: '#ffffff' },
    })).then(setQrCodeUrl)
  }, [])

  // Pre-event phase: show Go Live view
  if (state.phase === 'pre-event') {
    return (
      <AnimatePresence mode="wait">
        <m.div
          key="pre-event-artist"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="h-full"
        >
          <GoLiveView event={state.event} visitorCount={state.visitorCount} onGoLive={actions.goLive} />
        </m.div>
      </AnimatePresence>
    )
  }

  // Handlers
  const handlePlayFromQueue = (request: DemoQueueRequest) => {
    actions.playFromQueue(request.id)
    toast.success(t('nowPlayingToast', { songTitle: request.song.title }))
  }

  const handlePlaySong = (song: DemoSong) => {
    const queueItem = sortedQueue.find(r => r.songId === song.id)
    if (queueItem) {
      actions.playFromQueue(queueItem.id)
    } else {
      actions.playSong(song.id)
    }
    toast.success(t('nowPlayingToast', { songTitle: song.title }))
  }

  const handleReplay = (song: DemoSong) => {
    handlePlaySong(song)
  }

  const handleConfirmSkip = () => {
    if (confirmSkipRequest) {
      actions.skipRequest(confirmSkipRequest.id)
      toast.success(t('requestSkipped'))
      setConfirmSkipRequest(null)
    }
  }

  const handleStartBreak = (durationMinutes: number) => {
    actions.startBreak(durationMinutes)
    toast.success(t('breakStarted', { minutes: durationMinutes }))
  }

  const handleActivateSetlist = (setlistId: string) => {
    actions.activateSetlist(setlistId)
    setSelectedSetlistId(setlistId)
  }

  const handleClearSetlist = () => {
    actions.clearSetlist()
    setSelectedSetlistId(null)
  }

  const handlePlayFromSetlist = (position: number) => {
    actions.playFromSetlist(position)
  }

  return (
    <m.div
      key="live-artist"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="h-full"
    >
      <ReactionBubblesStyles />

      <div className="h-full bg-zinc-950 text-white relative">
        <div className="h-full overflow-auto">
        <ReactionBubbles reactions={state.recentReactions} />
        <div className="p-3 sm:p-4 space-y-4">
          <StatsHeader
            event={state.event}
            settings={state.settings}
            visitorCount={state.visitorCount}
            queueLength={sortedQueue.length}
            reactionCount={state.recentReactions.length}
            totalEarningsCents={state.totalEarningsCents}
            breakOnBreak={state.breakStatus.onBreak}
            notesCount={state.notes.length}
            activeTimerCount={activeTimers.length}
            onEndBreak={actions.endBreak}
            onOpenBreakDialog={() => setIsBreakDialogOpen(true)}
            onOpenNotes={() => setIsNotesOpen(true)}
            onOpenTimerDialog={() => setIsTimerDialogOpen(true)}
            onOpenQR={() => setIsQRDialogOpen(true)}
            onOpenSettings={() => setIsSettingsOpen(true)}
            onOpenEndEvent={() => setIsEndEventDialogOpen(true)}
          />

          <BreakControls
            breakStatus={state.breakStatus}
            isDialogOpen={isBreakDialogOpen}
            onSetDialogOpen={setIsBreakDialogOpen}
            onStartBreak={handleStartBreak}
            onEndBreak={actions.endBreak}
          />

          <TimersPanel
            activeTimers={activeTimers}
            currentTime={state.currentTime}
            isTimerDialogOpen={isTimerDialogOpen}
            onSetTimerDialogOpen={setIsTimerDialogOpen}
            onStartTimer={actions.startTimer}
            onCancelTimer={actions.cancelTimer}
            onExtendTimer={actions.extendTimer}
            onEditTimer={actions.editTimer}
          />

          <NowPlayingControls
            nowPlaying={state.nowPlaying}
            lastPlayed={state.lastPlayed}
            playedSongs={state.playedSongs}
            isSongHistoryOpen={isSongHistoryOpen}
            onSetSongHistoryOpen={setIsSongHistoryOpen}
            onEndCurrentSong={actions.endCurrentSong}
            onReplay={handleReplay}
          />

          <SetlistPanel
            eventSetlist={state.activeSetlist}
            setlists={state.setlists}
            nowPlaying={state.nowPlaying}
            selectedSetlistId={selectedSetlistId}
            onSelectSetlist={handleActivateSetlist}
            onClearSetlist={handleClearSetlist}
            onPlaySong={handlePlayFromSetlist}
          />

          <QueueManagement
            queue={sortedQueue}
            confirmSkipRequest={confirmSkipRequest}
            isAllRequestsOpen={isAllRequestsOpen}
            onSetConfirmSkipRequest={setConfirmSkipRequest}
            onSetAllRequestsOpen={setIsAllRequestsOpen}
            onPlayFromQueue={handlePlayFromQueue}
            onConfirmSkip={handleConfirmSkip}
          />

          <RepertoireSearch
            songs={state.songs}
            queue={sortedQueue}
            playedSongs={state.playedSongs}
            nowPlaying={state.nowPlaying}
            squelchedSongs={state.squelchedSongs}
            onPlaySong={handlePlaySong}
            onSquelch={actions.squelchSong}
            onUnsquelch={actions.unsquelchSong}
          />
        </div>
        </div>

        {/* QR Code Overlay */}
        {isQRDialogOpen && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-xs w-full mx-4 shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <h3 className="text-white font-semibold">{t('shareYourPage')}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsQRDialogOpen(false)}
                  className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-col items-center gap-4 p-4">
                {qrCodeUrl && (
                  <div className="p-3 bg-white rounded-xl">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={qrCodeUrl} alt="QR Code" className="w-44 h-44" />
                  </div>
                )}
                <p className="text-xs text-zinc-500 text-center">
                  {t('scanToTryDemo')}
                </p>
                <div className="flex items-center gap-2 w-full">
                  <div className="flex-1 px-3 py-2 bg-zinc-800 rounded-lg text-zinc-300 text-sm truncate">
                    iperform.live/demo
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Copy demo link"
                    className="border-zinc-700 shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/demo`)
                      toast.success(t('linkCopied'))
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* End Event Confirmation */}
        <InlineDialog open={isEndEventDialogOpen} onOpenChange={setIsEndEventDialogOpen}>
          <InlineDialogHeader onClose={() => setIsEndEventDialogOpen(false)}>
            <InlineDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {t('endLiveEvent')}
            </InlineDialogTitle>
            <InlineDialogDescription>
              {t('endEventDescription')}
            </InlineDialogDescription>
          </InlineDialogHeader>
          <InlineDialogFooter>
            <Button variant="outline" onClick={() => setIsEndEventDialogOpen(false)} className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">{t('keepLive')}</Button>
            <Button onClick={() => { setIsEndEventDialogOpen(false); toast.success(t('eventEnded')) }} className="bg-red-600 hover:bg-red-700 text-white">{t('endEventConfirm')}</Button>
          </InlineDialogFooter>
        </InlineDialog>

        <SettingsPanel
          isOpen={isSettingsOpen}
          onOpenChange={setIsSettingsOpen}
          settings={state.settings}
          onToggleSetting={(key) => {
            actions.toggleSetting(key)
            demoSession?.trackMilestone('setting_toggled', {
              setting: key,
              newValue: !state.settings[key],
            })
          }}
        />

        <NotesPanel
          isOpen={isNotesOpen}
          onOpenChange={setIsNotesOpen}
          notes={state.notes}
          nowPlaying={state.nowPlaying}
          onCreateNote={actions.createNote}
          onDeleteNote={actions.deleteNote}
        />
      </div>
    </m.div>
  )
}
